import React, { useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import client from "../../../api/client";
import { spreadPages } from "../../SendToClientPopup";

/**
 * Правки клиента к отправленной ему вёрстке (book_proofs, статус «changes»):
 * по каждому прокомментированному развороту — превью страниц и комментарий
 * под ним. Разворот 0 — обложка. Показывается сверху на странице версии.
 */

export interface ProofWithComments {
  id: number;
  versionId: number | null;
  source: string;
  status: string;
  createdAt: string;
  respondedAt: string | null;
  staffName: string | null;
  pages: number;
  hasBook: boolean;
  hasCover: boolean;
  comments: { spread: number; text: string }[];
}

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const ProofComments: React.FC<{ proof: ProofWithComments }> = ({ proof }) => {
  const [bookUrl, setBookUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  useEffect(() => {
    const urls: string[] = [];
    let cancelled = false;
    const load = async (kind: "book" | "cover", set: (u: string) => void) => {
      try {
        const res = await client.get(`/proofs/admin/${proof.id}/file/${kind}`, { responseType: "blob" });
        if (cancelled) return;
        const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        urls.push(u);
        set(u);
      } catch {
        /* превью нет — остаётся только текст */
      }
    };
    const needCover = proof.hasCover && proof.comments.some((c) => c.spread === 0);
    const needBook = proof.hasBook && proof.comments.some((c) => c.spread > 0);
    if (needBook) load("book", setBookUrl);
    if (needCover) load("cover", setCoverUrl);
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [proof]);

  const comments = [...proof.comments].sort((a, b) => a.spread - b.spread);

  return (
    <div className="ep-proof">
      <div className="ep-proof-head">
        <span className="ep-proof-title">Правки клиента</span>
        <span className="ep-proof-meta">
          к отправке от {fmt(proof.createdAt)}
          {proof.staffName ? ` (${proof.staffName})` : ""} · прислал {fmt(proof.respondedAt)}
        </span>
      </div>
      <div className="ep-proof-list">
        {comments.map((c, i) => {
          const [l, r] = spreadPages(c.spread - 1, proof.pages);
          return (
            <div className="ep-proof-item" key={i}>
              <div className="ep-proof-spread-label">
                {c.spread === 0 ? "Обложка" : `Разворот ${[l, r].filter(Boolean).join("–") || c.spread}`}
              </div>
              <div className="ep-proof-pages">
                {c.spread === 0 ? (
                  coverUrl && <CoverPage url={coverUrl} />
                ) : (
                  bookUrl && (
                    <>
                      <BookPage url={bookUrl} page={l} />
                      <BookPage url={bookUrl} page={r} />
                    </>
                  )
                )}
              </div>
              <div className="ep-proof-text">{c.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Каждая страница — в своём Document: файлы уже скачаны (blob-url), а
// комментариев обычно немного.
const BookPage: React.FC<{ url: string; page: number | null }> = ({ url, page }) => (
  <div className="ep-proof-page">
    {page && (
      <Document file={url} loading={null} error={null}>
        <Page pageNumber={page} height={190} renderAnnotationLayer={false} renderTextLayer={false} />
      </Document>
    )}
  </div>
);

const CoverPage: React.FC<{ url: string }> = ({ url }) => (
  <Document file={url} loading={null} error={null}>
    <Page pageNumber={1} height={190} renderAnnotationLayer={false} renderTextLayer={false} />
  </Document>
);

export default ProofComments;
